import { createClient } from '@supabase/supabase-js'

// Supabase credentials - injected by Lovable or from .env for local dev
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseKey) {
  console.error('Missing Supabase environment variables. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY')
}

export const supabase = createClient(supabaseUrl || '', supabaseKey || '')

// Live account ID — single source of truth for all account-bound queries
export const LIVE_ACCOUNT_ID = '11501732'

// Types based on JGL Capital schema
export interface Position {
  id: number
  account_id: string
  ticker: string
  quantity: number
  avg_cost: number
  current_price: number
  unrealized_pnl: number
  market_value: number
  position_pct: number
  sleeve: 'stock' | 'etf' | 'ipo' | 'commod' | 'crypto'
  entry_date: string | null
  entry_signal: string | null
  stop_price: number | null
  vwap_stop: number | null
  extreme_stop: number | null
  synced_at: string
}

export interface TrailingPerfWindow {
  portfolio_roc: number | null
  rsp_roc: number | null
  alpha: number | null
  days_actual: number
  baseline_date: string | null
  pnl_dollars: number | null
}

export interface PortfolioSnapshot {
  id: number
  snapshot_date: string
  account_id: string
  net_worth: number
  cash: number
  positions_value: number
  total_exposure_pct: number
  stock_exposure_pct: number
  etf_exposure_pct: number
  ipo_exposure_pct: number
  commod_exposure_pct?: number | null
  crypto_exposure_pct?: number | null
  position_count: number
  stock_position_count: number | null
  etf_position_count: number | null
  ipo_position_count: number | null
  unrealized_pnl: number | null
  daily_pnl: number
  daily_pnl_pct: number
  drawdown_pct: number
  high_water_mark: number
  open_risk_pct: number | null
  is_live: boolean | null
  trading_days_gap: number | null
  heartbeat_at: string | null
  trailing_perf: { t5?: TrailingPerfWindow; t10?: TrailingPerfWindow; t20?: TrailingPerfWindow; t60?: TrailingPerfWindow } | null
}

export interface SignalState {
  id: number
  ticker: string
  date: string
  rs_check: boolean
  rs_diff: number | null
  vwap_from_low: number | null
  vwap_qual: boolean
  pct_chg: number | null
  pct_chg_check: boolean
  mom_check: boolean
  price_check: boolean
  vol_check: boolean
  rsp_over_vwap: boolean
  trend_qual: boolean
  entry_signal: string | null
}

export interface Symbol {
  id: number
  ticker: string
  name: string | null
  asset_class: string | null
  strategy_group: string | null
  sleeve: string | null
  tier: number | null
  is_active: boolean
  ipo_date: string | null
}

// range_gate_* fields are DIAGNOSTIC ONLY since D079 (the dynamic range gate no longer gates entries).
export interface DynamicTqSleeveParams {
  range_gate_ref: number
  range_gate_k: number
  range_ratio: number
  regime_bucket: 'compressed' | 'normal' | 'expanded'
  efficiency_ratio: number
  benchmark: string
}

// Regime state — pre-market regime classification for entry sizing per sleeve
export interface RegimeState {
  id: number
  computed_date: string
  computed_at: string
  breadth_score: number | null
  spy_above_200sma: boolean | null
  regime_scalar: number | null   // stock + ETF scalar
  tier: number | null
  universe_count: number | null
  entry_count: number | null
  strong_count: number | null
  ipo_scalar: number | null
  ipo_rs_vs_spy: number | null
  commod_scalar: number | null
  commod_breadth: number | null
  crypto_scalar?: number | null
  crypto_breadth?: number | null
  dynamic_tq_params?: Record<string, DynamicTqSleeveParams> | null
  notes: string | null
}

// Strategy config — sourced live from Python constants via publish_strategy_config.py
// Table: jgl_strategy_config (config_key, value JSONB, source_file, source_var, updated_at)
export interface StrategyConfig {
  universe_filters?: {
    etf?: {
      avg_volume_50d_min?: number; aum_min?: number; atr_pct_min?: number
      spread_pct_max?: number; price_floor?: number; inception_days_min?: number; avg_gap_pct_max?: number
    }
    stock?: {
      market_cap_min?: number; avg_volume_5d_min?: number; avg_volume_20d_min?: number
      atr_pct_min?: number; atr_pct_max?: number; price_floor?: number
      days_since_ipo_min?: number; short_interest_max?: number; avg_gap_pct_max?: number
    }
    ipo?: {
      days_since_ipo_min?: number; days_since_ipo_max?: number; avg_volume_10d_min?: number
      price_floor?: number; market_cap_min?: number; atr_pct_min?: number; atr_pct_max?: number; avg_gap_pct_max?: number
    }
    commod?: { avg_volume_50d_min?: number; aum_min?: number; atr_pct_min?: number; avg_gap_pct_max?: number }
    crypto?: { avg_volume_50d_min?: number; aum_min?: number; atr_pct_min?: number; avg_gap_pct_max?: number }
  }
  sleeve_target_pct?: Record<string, number>
  percent_share?: Record<string, number>
  dual_cap_params?: Record<string, { capital_cap: number; risk_cap: number }>
  sleeve_multipliers?: Record<string, number>
  atr_period?: number
  bars_back?: number
  range_lookback?: number
  range_252d?: number
  range_scalar_floor?: number
  range_scalar_cap?: number
  sleeve_gate_k?: Record<string, number>
  sleeve_benchmarks?: Record<string, string>
  sleeve_base_activation?: Record<string, number>
  etf_universe?: string[]
  p22k_gate_params?: {
    adx_period?: number
    adx_gate?: string
    rs_gate_pct?: number
    cooldown_days?: number
    sleeves_with_rs_gate?: string[]
    sleeves_with_adx_gate?: string[]
    sleeves_with_cooldown?: string[]
  }
  _sources?: Record<string, { source_file: string; source_var: string; updated_at: string }>
}

export async function fetchStrategyConfig(): Promise<StrategyConfig | null> {
  // Filter to prod variant — jgl_strategy_config also holds sandbox_* variants
  // (publish_strategy_config.py --variant sandbox_<name>). Without this filter the
  // query mixes prod + sandbox rows and the last row per config_key wins, which
  // would silently display sandbox config as production.
  const { data, error } = await supabase
    .from('jgl_strategy_config')
    .select('config_key, value, source_file, source_var, updated_at')
    .eq('variant_id', 'prod')

  if (error || !data || data.length === 0) {
    if (error) console.error('Error fetching strategy config:', error)
    return null
  }

  const config: StrategyConfig = {}
  const sources: Record<string, { source_file: string; source_var: string; updated_at: string }> = {}

  for (const row of data) {
    (config as Record<string, unknown>)[row.config_key] = row.value
    sources[row.config_key] = {
      source_file: row.source_file ?? '',
      source_var: row.source_var ?? '',
      updated_at: row.updated_at,
    }
  }
  config._sources = sources
  return config
}

// Circuit breaker thresholds (from D001)
export const CIRCUIT_BREAKERS = {
  dailyLossHalt: -4,
  dailyLossReduce: -6,
  drawdown: {
    level1: { threshold: -10, leverage: 100 },
    level2: { threshold: -15, leverage: 75 },
    level3: { threshold: -20, leverage: 50 },
    level4: { threshold: -25, leverage: 25 },
  },
  sectorCaps: {
    tech: 50,
    other: 20,
  },
}

// Data fetching functions
export async function fetchLatestSnapshot(): Promise<PortfolioSnapshot | null> {
  const { data, error } = await supabase
    .from('jgl_portfolio_snapshots')
    .select('*')
    .eq('account_id', LIVE_ACCOUNT_ID)
    .order('snapshot_date', { ascending: false })
    .limit(1)
    .single()

  if (error) {
    console.error('Error fetching snapshot:', error)
    return null
  }
  return data
}

export async function fetchSnapshots(days: number = 30): Promise<PortfolioSnapshot[]> {
  const { data, error } = await supabase
    .from('jgl_portfolio_snapshots')
    .select('*')
    .eq('account_id', LIVE_ACCOUNT_ID)
    .order('snapshot_date', { ascending: false })
    .limit(days)

  if (error) {
    console.error('Error fetching snapshots:', error)
    return []
  }
  return data?.reverse() || []
}

export async function fetchPositions(): Promise<Position[]> {
  const { data, error } = await supabase
    .from('jgl_positions')
    .select('*')
    .eq('account_id', LIVE_ACCOUNT_ID)
    .eq('position_status', 'active')
    .order('market_value', { ascending: false })

  if (error) {
    console.error('Error fetching positions:', error)
    return []
  }
  return data || []
}

export async function fetchRegimeState(): Promise<RegimeState | null> {
  const { data, error } = await supabase
    .from('jgl_regime_state')
    .select('*')
    .order('computed_date', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (error) {
    console.error('Error fetching regime state:', error)
    return null
  }
  return data
}

export async function fetchSignalStates(): Promise<SignalState[]> {
  const { data, error } = await supabase
    .from('jgl_signal_states')
    .select('*')
    .order('date', { ascending: false })
    .limit(100)

  if (error) {
    console.error('Error fetching signals:', error)
    return []
  }
  return data || []
}

// Trade Journal types
export interface JournalEntry {
  id: number
  account_id: string | null
  trade_id: number | null
  ticker: string | null
  entry_type: string
  trade_date: string
  thesis: string | null
  post_review: string | null
  emotion_before: string | null
  emotion_after: string | null
  setup_quality: number | null
  rule_adherence: number | null
  execution_quality: number | null
  tags: string[]
  outcome: string | null
  pnl_at_review: number | null
  created_at: string
  updated_at: string
}

export type JournalEntryInsert = Omit<JournalEntry, 'id' | 'created_at' | 'updated_at'>

export const DEMO_JOURNAL: JournalEntry[] = [
  {
    id: 1, account_id: 'demo', trade_id: null, ticker: 'NVDA', entry_type: 'trade_review',
    trade_date: '2024-01-20', thesis: 'Strong momentum breakout above 52-week high. Semiconductors leading the market.',
    post_review: 'Entered at the right time. Could have sized up given the conviction level.',
    emotion_before: 'confident', emotion_after: 'calm', setup_quality: 5, rule_adherence: 4,
    execution_quality: 4, tags: ['momentum', 'breakout', 'semis'], outcome: 'win', pnl_at_review: 885,
    created_at: '2024-01-20T10:00:00Z', updated_at: '2024-01-20T10:00:00Z',
  },
  {
    id: 2, account_id: 'demo', trade_id: null, ticker: 'PLTR', entry_type: 'trade_review',
    trade_date: '2024-01-19', thesis: 'Trend entry on pullback to VWAP support. AI/defense narrative.',
    post_review: 'Entered too early — should have waited for confirmation candle. Stop is close.',
    emotion_before: 'anxious', emotion_after: 'fearful', setup_quality: 3, rule_adherence: 3,
    execution_quality: 2, tags: ['trend-entry', 'ai', 'pullback'], outcome: 'loss', pnl_at_review: -140,
    created_at: '2024-01-19T14:00:00Z', updated_at: '2024-01-19T14:00:00Z',
  },
  {
    id: 3, account_id: 'demo', trade_id: null, ticker: 'SPY', entry_type: 'trade_review',
    trade_date: '2024-01-18', thesis: 'Core ETF allocation — broad market trend following.',
    post_review: 'Steady position. Market internals supportive. Holding with conviction.',
    emotion_before: 'calm', emotion_after: 'calm', setup_quality: 4, rule_adherence: 5,
    execution_quality: 5, tags: ['core', 'etf', 'trend'], outcome: 'win', pnl_at_review: 365,
    created_at: '2024-01-18T09:00:00Z', updated_at: '2024-01-18T09:00:00Z',
  },
  {
    id: 4, account_id: 'demo', trade_id: null, ticker: 'CAVA', entry_type: 'trade_review',
    trade_date: '2024-01-18', thesis: 'IPO breakout — strong same-store sales growth, consumer discretionary momentum.',
    post_review: 'Position underwater. Held through earnings noise. Need better IPO discipline.',
    emotion_before: 'greedy', emotion_after: 'anxious', setup_quality: 3, rule_adherence: 2,
    execution_quality: 2, tags: ['ipo', 'consumer', 'earnings'], outcome: 'loss', pnl_at_review: -280,
    created_at: '2024-01-18T15:00:00Z', updated_at: '2024-01-18T15:00:00Z',
  },
  {
    id: 5, account_id: 'demo', trade_id: null, ticker: 'META', entry_type: 'trade_review',
    trade_date: '2024-01-17', thesis: 'Trend continuation — AI capex narrative, strong ad revenue.',
    post_review: 'Good entry timing. Following the system exactly as designed.',
    emotion_before: 'neutral', emotion_after: 'confident', setup_quality: 4, rule_adherence: 5,
    execution_quality: 4, tags: ['trend', 'ai', 'mega-cap'], outcome: 'win', pnl_at_review: 441,
    created_at: '2024-01-17T11:00:00Z', updated_at: '2024-01-17T11:00:00Z',
  },
  {
    id: 6, account_id: 'demo', trade_id: null, ticker: null, entry_type: 'daily_reflection',
    trade_date: '2024-01-16', thesis: 'Market showed mixed internals today. Advance/decline line weakening but trend still intact. Staying disciplined with current positions.',
    post_review: null, emotion_before: 'neutral', emotion_after: 'calm', setup_quality: null,
    rule_adherence: 5, execution_quality: null, tags: ['reflection', 'market-internals'], outcome: null,
    pnl_at_review: null, created_at: '2024-01-16T16:00:00Z', updated_at: '2024-01-16T16:00:00Z',
  },
]

export async function fetchJournalEntries(limit: number = 50): Promise<JournalEntry[]> {
  const { data, error } = await supabase
    .from('jgl_trade_journal')
    .select('*')
    .eq('account_id', LIVE_ACCOUNT_ID)
    .order('trade_date', { ascending: false })
    .limit(limit)

  if (error) {
    console.error('Error fetching journal entries:', error)
    return []
  }
  return data || []
}

export async function upsertJournalEntry(entry: Partial<JournalEntryInsert> & { id?: number }): Promise<JournalEntry | null> {
  const { data, error } = await supabase
    .from('jgl_trade_journal')
    .upsert({ ...entry, updated_at: new Date().toISOString() })
    .select()
    .single()

  if (error) {
    console.error('Error upserting journal entry:', error)
    return null
  }
  return data
}

export async function fetchActiveSymbols(): Promise<Symbol[]> {
  const { data, error } = await supabase
    .from('jgl_symbols')
    .select('*')
    .eq('is_active', true)
    .order('ticker')

  if (error) {
    console.error('Error fetching symbols:', error)
    return []
  }
  return data || []
}

// --- Strategy View Types ---

export interface PriceBar {
  date: string
  open: number
  high: number
  low: number
  close: number
  volume: number
}

export interface DailyScore {
  date: string
  score: number
  trend_direction: number
  trend_strength: number
  momentum: number
  volume: number
  relative_strength: number
  close: number
  sma_50: number | null
  sma_200: number | null
  rsi: number | null
}

export interface BacktestTrade {
  entry_date: string
  exit_date: string
  entry_price: number
  exit_price: number
  entry_score: number
  exit_score: number
  pnl: number
  pnl_pct: number
  bars_held: number
  outcome: 'win' | 'loss'
}

export interface BacktestMetricsData {
  total_return: number
  cagr: number
  sharpe: number
  max_drawdown: number
  win_rate: number
  profit_factor: number
  avg_bars: number
  total_trades: number
  avg_win: number
  avg_loss: number
  payoff_ratio: number
  expectancy: number
}

export interface BacktestResult {
  symbol: string
  strategy: string
  run_date: string
  metrics: BacktestMetricsData
  parameters: {
    daily_scores: DailyScore[]
    trades: BacktestTrade[]
    equity_curve: number[]
    weights: Record<string, number>
    thresholds: { entry: number; strong: number; exit: number }
  }
}

export async function fetchBacktestResults(
  strategy: string = 'trendqual',
  symbols: string[] = ['SPY', 'DIA', 'QQQ']
): Promise<BacktestResult[]> {
  const { data, error } = await supabase
    .from('jgl_backtests')
    .select('symbol, strategy, run_date, metrics, parameters')
    .eq('strategy', strategy)
    .in('symbol', symbols)

  if (error) {
    console.error('Error fetching backtest results:', error)
    return []
  }
  return (data || []) as BacktestResult[]
}

export async function fetchPriceData(
  symbol: string,
  days: number = 365
): Promise<PriceBar[]> {
  const cutoff = new Date()
  cutoff.setDate(cutoff.getDate() - (days + 10))
  const cutoffStr = cutoff.toISOString().split('T')[0]

  const { data, error } = await supabase
    .from('jgl_price_data')
    .select('date, open, high, low, close, volume')
    .eq('ticker', symbol)
    .gte('date', cutoffStr)
    .order('date', { ascending: false })
    .limit(days)

  if (error) {
    console.error('Error fetching price data:', error)
    return []
  }
  return (data || []).reverse() as PriceBar[]
}

// --- Demo Data for Strategy View (used when Supabase has no data) ---

function generateDemoScores(count: number): DailyScore[] {
  const scores: DailyScore[] = []
  const baseDate = new Date('2024-06-01')
  let price = 440
  let sma50 = 435
  let sma200 = 420

  for (let i = 0; i < count; i++) {
    const date = new Date(baseDate)
    date.setDate(date.getDate() + i)
    if (date.getDay() === 0 || date.getDay() === 6) continue

    const noise = (Math.random() - 0.48) * 4
    price += noise
    sma50 += noise * 0.3
    sma200 += noise * 0.1

    const direction = price > sma50 ? (price > sma200 && sma50 > sma200 ? 100 : 66) : 33
    const strength = 25 + Math.random() * 75
    const momentum = 30 + Math.random() * 60
    const vol = 30 + Math.random() * 50
    const rs = 40 + Math.random() * 40

    const score = direction * 0.25 + strength * 0.20 + momentum * 0.20 + vol * 0.15 + rs * 0.20
    const rsi = 35 + Math.random() * 35

    scores.push({
      date: date.toISOString().split('T')[0],
      score: Math.round(score * 10) / 10,
      trend_direction: Math.round(direction * 10) / 10,
      trend_strength: Math.round(strength * 10) / 10,
      momentum: Math.round(momentum * 10) / 10,
      volume: Math.round(vol * 10) / 10,
      relative_strength: Math.round(rs * 10) / 10,
      close: Math.round(price * 100) / 100,
      sma_50: Math.round(sma50 * 100) / 100,
      sma_200: Math.round(sma200 * 100) / 100,
      rsi: Math.round(rsi * 10) / 10,
    })
  }
  return scores
}

function generateDemoTrades(): BacktestTrade[] {
  return [
    { entry_date: '2024-07-15', exit_date: '2024-08-02', entry_price: 445.20, exit_price: 458.30, entry_score: 72.5, exit_score: 38.2, pnl: 1310, pnl_pct: 2.94, bars_held: 14, outcome: 'win' },
    { entry_date: '2024-08-19', exit_date: '2024-08-28', entry_price: 452.10, exit_price: 447.80, entry_score: 68.1, exit_score: 35.6, pnl: -430, pnl_pct: -0.95, bars_held: 7, outcome: 'loss' },
    { entry_date: '2024-09-10', exit_date: '2024-10-01', entry_price: 449.50, exit_price: 462.70, entry_score: 76.3, exit_score: 42.1, pnl: 1320, pnl_pct: 2.94, bars_held: 15, outcome: 'win' },
    { entry_date: '2024-10-14', exit_date: '2024-10-22', entry_price: 460.80, exit_price: 455.20, entry_score: 65.8, exit_score: 37.4, pnl: -560, pnl_pct: -1.22, bars_held: 6, outcome: 'loss' },
    { entry_date: '2024-11-05', exit_date: '2024-12-02', entry_price: 458.30, exit_price: 478.90, entry_score: 82.1, exit_score: 39.8, pnl: 2060, pnl_pct: 4.49, bars_held: 19, outcome: 'win' },
  ]
}

export const DEMO_BACKTEST: Record<string, BacktestResult> = {
  SPY: {
    symbol: 'SPY',
    strategy: 'trendqual',
    run_date: new Date().toISOString(),
    metrics: {
      total_return: 62.8, cagr: 8.38, sharpe: -0.10, max_drawdown: -20.9,
      win_rate: 36.6, profit_factor: 1.27, avg_bars: 13.7, total_trades: 3049,
      avg_win: 4.12, avg_loss: -2.35, payoff_ratio: 1.75, expectancy: 0.02,
    },
    parameters: {
      daily_scores: generateDemoScores(180),
      trades: generateDemoTrades(),
      equity_curve: Array.from({ length: 180 }, (_, i) => 100000 + i * 50 + (Math.random() - 0.45) * 2000),
      weights: { trend_direction: 0.25, trend_strength: 0.20, momentum: 0.20, volume: 0.15, relative_strength: 0.20 },
      thresholds: { entry: 65, strong: 80, exit: 40 },
    },
  },
  DIA: {
    symbol: 'DIA',
    strategy: 'trendqual',
    run_date: new Date().toISOString(),
    metrics: {
      total_return: 48.2, cagr: 6.72, sharpe: 0.15, max_drawdown: -18.3,
      win_rate: 38.2, profit_factor: 1.19, avg_bars: 12.1, total_trades: 2847,
      avg_win: 3.85, avg_loss: -2.48, payoff_ratio: 1.55, expectancy: 0.01,
    },
    parameters: {
      daily_scores: generateDemoScores(180),
      trades: generateDemoTrades(),
      equity_curve: Array.from({ length: 180 }, (_, i) => 100000 + i * 40 + (Math.random() - 0.45) * 1800),
      weights: { trend_direction: 0.25, trend_strength: 0.20, momentum: 0.20, volume: 0.15, relative_strength: 0.20 },
      thresholds: { entry: 65, strong: 80, exit: 40 },
    },
  },
  QQQ: {
    symbol: 'QQQ',
    strategy: 'trendqual',
    run_date: new Date().toISOString(),
    metrics: {
      total_return: 78.5, cagr: 10.12, sharpe: 0.32, max_drawdown: -24.1,
      win_rate: 35.1, profit_factor: 1.35, avg_bars: 14.8, total_trades: 3215,
      avg_win: 5.10, avg_loss: -2.72, payoff_ratio: 1.88, expectancy: 0.03,
    },
    parameters: {
      daily_scores: generateDemoScores(180),
      trades: generateDemoTrades(),
      equity_curve: Array.from({ length: 180 }, (_, i) => 100000 + i * 65 + (Math.random() - 0.45) * 2500),
      weights: { trend_direction: 0.25, trend_strength: 0.20, momentum: 0.20, volume: 0.15, relative_strength: 0.20 },
      thresholds: { entry: 65, strong: 80, exit: 40 },
    },
  },
}